import { useEffect, useRef, useCallback } from "react";
import movementSound from "../assets/sounds/Movement-FX.wav";
import selectionSound from "../assets/sounds/Selection-FX.wav";
import backSound from "../assets/sounds/Back-FX.wav";
import { isAndroidAppTv, isTalkBackEnabled } from "../utils/platform";
import { useAppSelector } from "../components/hooks";
import { useCurrentProfile } from "./useProfileSession";
import { getSoundStorageKey, STORAGE_SOUND } from "../models/storage";

export type SFXType = "movement" | "selection" | "back";

const soundSources: Record<SFXType, string> = {
  movement: movementSound,
  selection: selectionSound,
  back: backSound,
};

/**
 * SFX playback hook
 * - Android TV native app: routes to AndroidBridge.playSFX
 * - Other platforms: Web Audio API with pre-decoded buffers
 */
export const useSFXSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBuffersRef = useRef<Record<string, AudioBuffer>>({});
  const { isMenuOpen, loadingStatus } = useAppSelector(
    (state) => state.gamePlay,
  );
  const isPlayingGame = !isMenuOpen && loadingStatus === "Done";
  const isPlayingGameRef = useRef(isPlayingGame);
  isPlayingGameRef.current = isPlayingGame;
  const { currentProfile } = useCurrentProfile();
  const soundStorageKey = getSoundStorageKey(currentProfile?.id);
  const isSoundOnRef = useRef(
    localStorage.getItem(soundStorageKey) !== "false",
  );

  useEffect(() => {
    const sync = () => {
      isSoundOnRef.current = localStorage.getItem(soundStorageKey) !== "false";
    };
    sync();
    window.addEventListener(STORAGE_SOUND, sync);
    return () => {
      window.removeEventListener(STORAGE_SOUND, sync);
    };
  }, [soundStorageKey]);

  useEffect(() => {
    if (isAndroidAppTv) return;

    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    audioContextRef.current = ctx;

    // Pre-load all sound buffers
    Object.entries(soundSources).forEach(([key, src]) => {
      fetch(src)
        .then((res) => res.arrayBuffer())
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => {
          audioBuffersRef.current[key] = decoded;
        })
        .catch(() => {});
    });

    return () => {
      ctx.close().catch(() => {});
    };
  }, []);

  const playSFX = useCallback((type: SFXType, force = false) => {
    if (!isSoundOnRef.current) return;
    if (isTalkBackEnabled()) return;
    if (isPlayingGameRef.current && type !== "selection" && !force) return;

    // Android TV native
    if (isAndroidAppTv && window.AndroidBridge?.playSFX) {
      window.AndroidBridge.playSFX(type);
      return;
    }

    const ctx = audioContextRef.current;
    const buffer = audioBuffersRef.current[type];
    if (ctx && buffer) {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    }
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Custom sound via data-button-sound attribute (e.g. "back", "selection")
      const customSound = target
        .closest("[data-button-sound]")
        ?.getAttribute("data-button-sound") as SFXType;
      if (customSound) {
        playSFX(customSound);
        return;
      }

      const isNonMenu = target.closest(
        "[data-gamepad]:not([data-gamepad^='menu-'])",
      );
      if (target.closest("[data-gamepad]") && (isNonMenu || e.isTrusted)) {
        playSFX("selection");
        return;
      }
    };

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [playSFX]);

  return playSFX;
};
